import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { editSize, EDIT_MAX_SIDE, filterSpecs, isEdited, isNoEdit, NO_EDIT, PRESETS, readEdit, shrinkType, UPLOAD_MAX_BYTES, type FilterSpec, type PhotoEdit } from '../editor/photo';
import { onGpuLost, peekOriginal, peekProblem, swapSrc, tapHint, watchGpu } from '../editor/picture';
import { assets } from '../editor/storage';
import { $, $in, setPressed } from './dom';
import { closeSheets, isOpen, openSheet } from './sheets';
import { toast } from './toast';

/*
 * "แต่งภาพ" (choice ก: a sheet with tabs) for the selected picture:
 *   ปรับแสงสี  sliders; the picture changes while dragging, one undo step when the finger lifts
 *   ฟิลเตอร์   ten looks shown on a small copy of the picture, and how strong
 *   คืนค่ารูปเดิม  clears the settings (the picture itself was never changed)
 *   ดูต้นฉบับ     held (choice 2ก): the picture shows without its look until the finger lifts — nothing is changed or
 *                 saved; also a chip in the picture's toolbar (src/ui/toolbar.ts)
 * A picture larger than EDIT_MAX_SIDE is first made that size (choice ก), with a message — Fabric's GPU filters cut
 * a bigger one off.
 */

type Pic = fabric.Image & { photoEdit?: unknown; _originalElement?: HTMLImageElement | HTMLCanvasElement };

const SLIDERS: Array<{ key: keyof PhotoEdit; name: string; min: number }> = [
  { key: 'brightness', name: 'ความสว่าง', min: -100 },
  { key: 'contrast', name: 'คอนทราสต์', min: -100 },
  { key: 'saturation', name: 'ความสดของสี', min: -100 },
  { key: 'warmth', name: 'โทนสี (เย็น ↔ อุ่น)', min: -100 },
  { key: 'sharpness', name: 'ความคมชัด', min: 0 },
  { key: 'blur', name: 'เบลอ', min: 0 }
];

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const makeFilters = (specs: FilterSpec[]) =>
  specs.map(s => new (fabric.Image.filters as unknown as Record<string, new (o: object) => fabric.IBaseFilter>)[s.type](s));

/** set the picture's filters from its settings and draw it again; a Thai reason when the device cannot */
export function applyEdit(img: Pic, e: PhotoEdit): string | null {
  const before = img.filters ?? [];
  try {
    img.filters = makeFilters(filterSpecs(e));
    img.applyFilters();
    watchGpu();
    img.set('photoEdit' as keyof fabric.Object, isNoEdit(e) ? undefined : { ...e });
    img.dirty = true;
    return null;
  } catch (err) {
    // put back what worked: filters that fail here would be saved and then fail again inside Fabric when the work opens
    img.filters = before;
    try { img.applyFilters(); } catch { /* the picture is drawn as it was last time */ }
    return `แต่งภาพไม่สำเร็จ (เครื่องนี้ประมวลผลภาพไม่ได้: ${errText(err)}) ลองปิดแอปอื่นแล้วลองใหม่`;
  }
}

/** `onEditedChanged`: the picture became edited or unedited (its chips show the mark and ดูต้นฉบับ only then) */
export function initPhotoSheet(ed: Editor, history: History, onEditedChanged: () => void = () => {}) {
  const { canvas } = ed;
  const msg = $('phMsg');
  let img: Pic | null = null;
  let cur: PhotoEdit = { ...NO_EDIT };
  let frame = 0;
  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };

  /* ---------- sliders (made once) ---------- */
  const rows = new Map<keyof PhotoEdit, { input: HTMLInputElement; value: HTMLElement }>();
  for (const s of SLIDERS) {
    const row = document.createElement('div'); row.className = 'phrow';
    const lbl = document.createElement('span'); lbl.className = 'lbl';
    const value = document.createElement('span');
    lbl.append(s.name, value);   // the name as text: `.lbl span` is the number font
    const input = document.createElement('input');
    input.type = 'range'; input.min = String(s.min); input.max = '100'; input.step = '1';
    input.setAttribute('aria-label', s.name);
    input.oninput = () => { (cur[s.key] as number) = Number(input.value); value.textContent = input.value; preview(); };
    input.onchange = () => commit();
    row.append(lbl, input);
    $('phAdjust').append(row);
    rows.set(s.key, { input, value });
  }
  const amount = $in('phAmount');
  amount.oninput = () => { cur.amount = Number(amount.value); $('phAmountV').textContent = `${amount.value}%`; preview(); };
  amount.onchange = () => commit();

  $('phTabs').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    setPressed('phTabs', x => x === b);
    $('phAdjust').hidden = b.dataset.v !== 'adjust';
    $('phLooks').hidden = b.dataset.v !== 'looks';
  };

  /** the picture follows the sliders at most once a frame while a finger moves */
  function preview() {
    if (!img || frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!img) return;
      const why = applyEdit(img, cur);
      if (why) say(why);
      canvas.requestRenderAll();
    });
  }
  /** one undo step for a finished change */
  function commit() {
    if (!img) return;
    const was = isEdited(img.photoEdit);
    const why = applyEdit(img, cur);
    if (why) { say(why); return; }
    canvas.requestRenderAll();
    history.push();
    if (was !== isEdited(img.photoEdit)) onEditedChanged();
  }

  function paint() {
    for (const s of SLIDERS) { const r = rows.get(s.key)!; r.input.value = String(cur[s.key]); r.value.textContent = String(cur[s.key]); }
    amount.value = String(cur.amount); $('phAmountV').textContent = `${cur.amount}%`;
    ($('phAmount').parentElement as HTMLElement).hidden = !cur.preset;
    for (const b of $('phGrid').querySelectorAll<HTMLButtonElement>('.phlook')) b.setAttribute('aria-pressed', String((b.dataset.v ?? '') === cur.preset));
  }

  /* ---------- looks: a small copy of the picture with each look ---------- */
  function looks(pic: Pic) {
    const grid = $('phGrid');
    grid.textContent = '';
    const src = pic._originalElement ?? (pic.getElement() as HTMLImageElement);
    const side = 160, w = src.width, h = src.height;
    const k = side / Math.max(w, h);
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(w * k)); small.height = Math.max(1, Math.round(h * k));
    try { small.getContext('2d')!.drawImage(src, 0, 0, small.width, small.height); }
    catch { /* thumbnails stay blank; the looks still work */ }
    const add = (id: string, name: string) => {
      const b = document.createElement('button'); b.className = 'phlook'; b.dataset.v = id;
      b.setAttribute('aria-label', `ฟิลเตอร์ ${name}`);
      const pic2 = document.createElement('img'); pic2.alt = '';
      try {
        const t = new fabric.Image(small);
        if (id) { t.filters = makeFilters(filterSpecs({ ...NO_EDIT, preset: id, amount: 100 })); t.applyFilters(); }
        pic2.src = (t.getElement() as unknown as HTMLCanvasElement).toDataURL?.('image/jpeg', 0.8) ?? small.toDataURL('image/jpeg', 0.8);
        t.dispose();   // its GPU copy is not needed any more
      } catch { try { pic2.src = small.toDataURL('image/jpeg', 0.8); } catch { /* blank thumbnail; the look still works */ } }
      b.append(pic2, Object.assign(document.createElement('span'), { textContent: name }));
      b.onclick = () => { cur.preset = id; if (id && !cur.amount) cur.amount = 100; paint(); commit(); };
      grid.append(b);
    };
    add('', 'ไม่ใส่');
    for (const p of PRESETS) add(p.id, p.name);
  }

  $('phReset').onclick = () => {
    if (!img) return;
    cur = { ...NO_EDIT };
    paint(); commit();
    say('คืนค่ารูปเดิมแล้ว', true);
  };

  /**
   * A picture larger than EDIT_MAX_SIDE: draw it at that size into a new file (kept with the work like an upload),
   * same place and size on the page. Refused with the reason when the device cannot.
   */
  async function shrink(pic: Pic): Promise<string | null | 'shrunk'> {
    const src = pic._originalElement ?? (pic.getElement() as HTMLImageElement);
    const w = (src as HTMLImageElement).naturalWidth || src.width, h = (src as HTMLImageElement).naturalHeight || src.height;
    if (Math.max(w, h) <= EDIT_MAX_SIDE) return null;
    const t = editSize(w, h);
    const c = document.createElement('canvas'); c.width = t.w; c.height = t.h;
    const ctx = c.getContext('2d');
    if (!ctx) return 'เครื่องนี้หน่วยความจำไม่พอสำหรับย่อรูป ลองปิดแอปอื่นแล้วลองใหม่';
    ctx.drawImage(src, 0, 0, t.w, t.h);
    // see-through parts stay see-through: the type comes from the file itself (a blob: address has no extension)
    const at = pic.getSrc();
    const own = assets.get(at) ?? (at.startsWith('blob:') ? await fetch(at).then(r => r.blob()).catch(() => undefined) : undefined);
    const type = shrinkType(own?.type, at);
    let blob: Blob | null;
    try { blob = await new Promise<Blob | null>(r => c.toBlob(r, type, 0.92)); }
    catch { return 'อ่านพิกเซลของรูปนี้ไม่ได้ (รูปมาจากเว็บอื่นที่ไม่อนุญาต) จึงแต่งภาพไม่ได้'; }
    if (!blob) return 'ย่อรูปไม่สำเร็จ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่';
    // the same limit the server checks: a bigger file could never be saved to the server (every save would fail)
    if (blob.size > UPLOAD_MAX_BYTES) {
      return `ย่อรูปแล้วไฟล์ยังใหญ่ ${(blob.size / 1024 / 1024).toFixed(1)} MB เกินที่เซิร์ฟเวอร์รับ ${UPLOAD_MAX_BYTES / 1024 / 1024} MB จึงแต่งภาพนี้ไม่ได้ (ลองใช้รูปแบบ JPG แทน PNG)`;
    }
    const url = URL.createObjectURL(blob);
    const sx = pic.scaleX ?? 1, sy = pic.scaleY ?? 1, ow = pic.width ?? w, oh = pic.height ?? h;
    const left = pic.left, top = pic.top;
    // load and check the smaller file first; the picture changes only when it opened (BUG-29)
    let swapped: boolean;
    try { swapped = await swapSrc(pic, url, 'เปิดรูปที่ย่อแล้วไม่ได้', t.w / w, at); }   // the kept part (ครอป) comes along
    catch (e) { URL.revokeObjectURL(url); return `${errText(e)} ลองใหม่อีกครั้ง`; }
    if (!swapped) { URL.revokeObjectURL(url); return 'รูปนี้เพิ่งถูกเปลี่ยนไฟล์ระหว่างย่อ (เช่น บันทึกขึ้นเซิร์ฟเวอร์) กด "แต่งภาพ" อีกครั้ง'; }
    assets.set(url, blob);
    pic.set({ scaleX: sx * ow / (pic.width ?? t.w), scaleY: sy * oh / (pic.height ?? t.h), left, top });
    pic.setCoords();
    toast(`รูปใหญ่ ${w}×${h} px ย่อเหลือ ${t.w}×${t.h} px เพื่อแต่งภาพ (ยังพิมพ์ได้คมราว ${Math.round(t.w / 300 * 2.54)} ซม. ที่ 300 dpi)`);
    return 'shrunk';
  }

  let busy = false;
  async function open() {
    const o = canvas.getActiveObject() as Pic | null;
    if (!o || o.type !== 'image' || busy) return;   // one picture made smaller at a time (a second tap waits)
    img = null; say('');
    busy = true;
    let why: string | null | 'shrunk';
    try { why = await shrink(o); }
    catch (e) { why = `แต่งภาพไม่ได้: ${errText(e)}`; }
    finally { busy = false; }
    if (why === 'shrunk') {
      // undo/page change while it was being made smaller: the picture is not on the page any more, nothing to record
      if (!canvas.getObjects().includes(o)) return;
      canvas.requestRenderAll(); history.push();
    } else if (why) { toast(why, true); return; }
    if (canvas.getActiveObject() !== o) { follow(); return; }   // another object chosen while it was being made smaller
    img = o;
    cur = readEdit(o.photoEdit);
    if (o.filters?.length && !o.photoEdit) {
      // filters from somewhere else (not made here): keep them until something is changed, and say so
      say('รูปนี้มีการปรับไว้จากที่อื่น ถ้าเปลี่ยนค่าในนี้ ค่าเดิมจะถูกแทนที่');
    }
    looks(o);
    paint();
    openSheet('shPhoto');
  }

  /** the selection changed (another object, undo): follow it, or close when it is not a picture any more */
  function follow() {
    if (!isOpen('shPhoto')) return;
    const o = canvas.getActiveObject() as Pic | null;
    if (!o || o.type !== 'image') { img = null; closeSheets(); return; }
    // another picture chosen with the sheet open: the same checks as opening (a big one is made smaller first)
    if (o !== img && !busy) void open();
  }

  // the iPad may take the GPU back (memory): Fabric does not notice and would draw edited pictures blank — switch to
  // its CPU filters, draw them again, and say so
  onGpuLost(() => {
    for (const o of canvas.getObjects()) {
      const p = o as Pic;
      if (p.type === 'image' && p.photoEdit) applyEdit(p, readEdit(p.photoEdit));
    }
    canvas.requestRenderAll();
    toast('หน่วยความจำกราฟิกของเครื่องเต็ม ระบบเปลี่ยนไปแต่งภาพแบบช้าลงแทน ภาพที่แต่งไว้ยังอยู่ครบ', true);
  });

  /* ---------- ดูต้นฉบับ (held) ---------- */
  let peeking: Pic | null = null, downAt = -1, started = false;
  function peekStop() {
    const p = peeking; peeking = null;
    if (p && peekOriginal(p, false)) canvas.requestRenderAll();
  }
  /** `el` shows the chosen picture's original while it is held; a quick tap says how it works */
  function holdOriginal(el: HTMLElement) {
    el.addEventListener('pointerdown', e => {
      e.preventDefault();
      downAt = e.timeStamp; started = false;
      peekStop();
      const o = canvas.getActiveObject() as Pic | null;
      const why = peekProblem(o, !!o && isEdited(o.photoEdit));
      if (why) { toast(why, true); return; }
      if (!peekOriginal(o!, true)) { toast('ยังแสดงต้นฉบับไม่ได้ ลองใหม่อีกครั้ง', true); return; }
      peeking = o; started = true; canvas.requestRenderAll();
    });
    el.addEventListener('click', e => {
      // keyboard (Enter/Space): there is no holding — say how it works
      if ((e as MouseEvent).detail === 0) { toast('ดูต้นฉบับใช้โดยกดค้างด้วยนิ้วหรือเมาส์', true); return; }
      if (tapHint(started, e.timeStamp - downAt)) toast('กดค้างไว้เพื่อดูต้นฉบับ ปล่อยนิ้วแล้วกลับเป็นแบบที่แต่ง');
    });
    el.addEventListener('contextmenu', e => e.preventDefault());   // a long press is ours, not the browser's menu
  }
  // let go anywhere (the chip may be drawn again under the finger), the app hidden, or the picture no longer chosen
  document.addEventListener('pointerup', peekStop, true);
  document.addEventListener('pointercancel', peekStop, true);
  document.addEventListener('visibilitychange', peekStop);
  window.addEventListener('blur', peekStop);
  canvas.on('selection:cleared', peekStop);
  canvas.on('selection:updated', peekStop);
  canvas.on('object:removed', peekStop);
  holdOriginal($('phPeek'));

  return { open, sync: follow, holdOriginal };
}
